import { useEffect, useRef, useState } from "react";
import { Mic, X, Save, Sparkles, Loader2, Calendar as CalIcon, AlertCircle } from "lucide-react";
import api, { formatErr } from "../lib/api";
import Modal from "./Modal";

/**
 * One-tap voice → entity creation.
 *
 * Flow (task kind):
 *   1. Tap mic → MediaRecorder → blob → /api/hidro/voice/transcribe → text
 *   2. AUTO: POST /api/tasks/parse-voice with the transcript. Hidro
 *      extracts title, description, assignee(s), priority, due_date and
 *      returns them.
 *   3. User reviews the pre-filled fields, tweaks anything, taps Create.
 *   4. onCreate(fields) is called with the full structured task.
 *
 * Flow (ticket kind — unchanged): no AI parse step; simply transcribes
 * and falls back to {title, description}.
 *
 * Trust Contract: if AI parse fails, the transcript is preserved as
 * description and the form still opens so the user never loses their
 * voice memo.
 */
export default function VoiceQuickCreate({ kind = "ticket", onCreate, onCreated, testId }) {
  const [open, setOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [transcript, setTranscript] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  // Task-only fields
  const [assignees, setAssignees] = useState([]); // [{id, name}]
  const [priority, setPriority] = useState("normal");
  const [dueDate, setDueDate] = useState("");
  const [unmatched, setUnmatched] = useState([]);
  const [confidence, setConfidence] = useState(0);

  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const isTask = kind === "task";

  const reset = () => {
    setRecording(false);
    setTranscribing(false);
    setParsing(false);
    setBusy(false);
    setErr("");
    setTranscript("");
    setTitle("");
    setDescription("");
    setAssignees([]);
    setPriority("normal");
    setDueDate("");
    setUnmatched([]);
    setConfidence(0);
    chunksRef.current = [];
    recRef.current = null;
  };

  const close = () => {
    if (recording) {
      try { recRef.current?.stop(); } catch (_e) { /* noop */ }
    }
    reset();
    setOpen(false);
  };

  useEffect(() => () => {
    if (recRef.current && recRef.current.state !== "inactive") {
      try { recRef.current.stop(); } catch (_e) { /* noop */ }
    }
  }, []);

  // AI parse — converts a free-form transcript into structured task fields.
  // Trust Contract: if the call fails, we still keep the transcript so
  // the user can edit and save manually.
  const parseTranscript = async (text) => {
    if (!isTask) {
      // Ticket path — same old behaviour (first sentence → title).
      const firstLine = text.split(/\.|\n/)[0]?.trim() || text;
      setTitle(firstLine.length > 80 ? `${firstLine.slice(0, 78)}…` : firstLine);
      setDescription(text);
      return;
    }
    setParsing(true);
    try {
      const { data } = await api.post("/tasks/parse-voice", { transcript: text }, { timeout: 30000 });
      setTitle(data.title || "");
      setDescription(data.description || text);
      setAssignees(
        (data.assigned_to_ids || []).map((id, i) => ({
          id,
          name: (data.assigned_to_names || [])[i] || id.slice(0, 8),
        })),
      );
      setPriority(data.priority || "normal");
      setDueDate(data.due_date || "");
      setUnmatched(data.unmatched_assignees || []);
      setConfidence(data.confidence || 0);
      if (data.warning) setErr(data.warning);
    } catch (e) {
      // Trust Contract fallback — still pre-fill from the transcript.
      const firstLine = text.split(/\.|\n/)[0]?.trim() || text;
      setTitle(firstLine.length > 80 ? `${firstLine.slice(0, 78)}…` : firstLine);
      setDescription(text);
      setErr(formatErr(e?.response?.data?.detail, "AI parse unavailable — edit fields below."));
    } finally {
      setParsing(false);
    }
  };

  const startRec = async () => {
    setErr("");
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // [Iter 290zp.13s — iOS Safari voice task fix]
      // iOS Safari's MediaRecorder does NOT support webm. The previous
      // code probed only audio/webm variants — both returned false on
      // iOS — then fell through to `mime = "audio/webm"` and constructed
      // the recorder with that. Safari silently ignores the requested
      // mimeType and records in audio/mp4 anyway. The resulting Blob
      // was tagged `audio/webm` (the LIE), the backend trusted it,
      // and Whisper got mp4 bytes on a .webm filename → empty result
      // → "Couldn't make out the audio".
      // Fix: probe a wider candidate list (mp4/ogg too) and ALWAYS use
      // `rec.mimeType` (the codec the recorder ACTUALLY chose) as
      // ground truth for the Blob type and upload extension.
      const candidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4;codecs=mp4a.40.2",
        "audio/mp4",
        "audio/ogg;codecs=opus",
        "audio/ogg",
      ];
      const requestedMime = candidates.find((m) => MediaRecorder.isTypeSupported(m)) || "";
      const recOpts = requestedMime ? { mimeType: requestedMime } : {};
      const rec = new MediaRecorder(stream, recOpts);
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        try {
          stream.getTracks().forEach((t) => t.stop());
          // Use the recorder's reported mimeType — that's what's actually
          // in the bytes, regardless of what we requested. Falls back to
          // the requested mime, then a sane default.
          const actualMime = rec.mimeType || requestedMime || "audio/webm";
          const blob = new Blob(chunksRef.current, { type: actualMime });
          if (blob.size === 0) {
            setErr("No audio captured. Try again.");
            return;
          }
          // Pick the file extension to match the actual codec so Whisper
          // sniffs the format correctly.
          const ext = actualMime.includes("mp4")
            ? "mp4"
            : actualMime.includes("m4a")
            ? "m4a"
            : actualMime.includes("ogg")
            ? "ogg"
            : actualMime.includes("wav")
            ? "wav"
            : "webm";
          setTranscribing(true);
          const fd = new FormData();
          fd.append("audio", blob, `voice-${kind}.${ext}`);
          const { data } = await api.post("/hidro/voice/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000,
          });
          const text = (data?.text || "").trim();
          if (!text) {
            setErr("Couldn't make out the audio. Try again or type it in.");
            return;
          }
          setTranscript(text);
          await parseTranscript(text);
        } catch (e) {
          setErr(formatErr(e?.response?.data?.detail, "Transcription failed."));
        } finally {
          setTranscribing(false);
        }
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (e) {
      setErr(`Microphone error: ${e?.message || "permission denied"}`);
    }
  };

  const stopRec = () => {
    try { recRef.current?.stop(); } catch (_e) { /* noop */ }
    setRecording(false);
  };

  const submit = async () => {
    if (!title.trim()) {
      setErr("Add a title before saving.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const payload = isTask
        ? {
            title: title.trim(),
            description: description.trim(),
            assigned_to_ids: assignees.map((a) => a.id),
            priority,
            due_date: dueDate || null,
            voice_transcript: transcript || null,
          }
        : { title: title.trim(), description: description.trim() };
      const created = await onCreate(payload);
      if (onCreated) await onCreated(created);
      reset();
      setOpen(false);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, `Could not create ${kind}.`));
    } finally {
      setBusy(false);
    }
  };

  const label = isTask ? "Voice task" : "Voice ticket";
  const removeAssignee = (id) => setAssignees((xs) => xs.filter((a) => a.id !== id));

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="hv-btn hv-btn-ghost text-xs"
        title={`Record a voice memo and turn it into a ${kind}`}
        data-testid={testId || `voice-quick-create-${kind}`}
      >
        <Mic className="w-4 h-4 text-[#1fb6c1]" /> {label}
      </button>

      <Modal open={open} onClose={close} title={`Voice → new ${kind}`} testId={`voice-${kind}-modal`} maxWidth="max-w-lg">
        <div className="space-y-3">
          <div className="text-xs text-[#a0aabf]">
            {isTask ? (
              <>Tap the mic and speak naturally, e.g. <em>&quot;Create a task for Ben to order switches for Thornybush tomorrow&quot;</em>. Hidro will fill in the title, assignee, due date, and priority for you to review.</>
            ) : (
              <>Tap the mic, describe the {kind} naturally, and tap stop. I&apos;ll transcribe it and pre-fill the form so you can tweak before saving.</>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {!recording && !transcribing && !parsing && (
              <button
                type="button"
                onClick={startRec}
                className="hv-btn hv-btn-primary text-sm"
                data-testid={`voice-${kind}-start`}
              >
                <Mic className="w-4 h-4" /> Start recording
              </button>
            )}
            {/* [Iter 290zp.31] The "Stop & transcribe" button previously
                lived here at the TOP of the modal. Users kept skipping
                past it and tapping the bottom submit button, which
                submitted an empty task and lost the whole recording.
                It's now removed — the ONLY stop control while
                recording is the bottom action bar, whose Save button
                is repurposed to "Done" during the recording state. */}
            {recording && (
              <div
                className="text-xs text-rose-300 italic flex items-center gap-1.5"
                data-testid={`voice-${kind}-listening`}
              >
                <Mic className="w-3 h-3 animate-pulse" />
                Listening… tap <strong className="not-italic text-rose-200">Done</strong> below when finished.
              </div>
            )}
            {transcribing && (
              <div className="text-xs text-[#1fb6c1] italic flex items-center gap-1" data-testid={`voice-${kind}-transcribing`}>
                <Loader2 className="w-3 h-3 animate-spin" /> Transcribing…
              </div>
            )}
            {parsing && (
              <div className="text-xs text-[#1fb6c1] italic flex items-center gap-1" data-testid={`voice-${kind}-parsing`}>
                <Sparkles className="w-3 h-3 animate-pulse" /> Hidro is filling in the task…
              </div>
            )}
            {isTask && confidence > 0 && !parsing && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold"
                style={{ background: confidence >= 70 ? "#10B98122" : "#F59E0B22", color: confidence >= 70 ? "#10B981" : "#F59E0B" }}
                data-testid={`voice-${kind}-confidence`}>
                AI {confidence}% confident
              </span>
            )}
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#647087]">Title *</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="hv-input mt-1 text-sm"
              placeholder={`Short ${kind} title…`}
              data-testid={`voice-${kind}-title`}
            />
          </div>

          {isTask && (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#647087]">Assigned to</label>
                <div className="mt-1 flex flex-wrap gap-1.5 min-h-[36px] p-2 rounded border border-white/10 bg-black/30" data-testid={`voice-${kind}-assignees`}>
                  {assignees.length === 0 && (
                    <span className="text-xs text-[#647087] italic">Nobody assigned — you can leave it for the team page picker.</span>
                  )}
                  {assignees.map((a) => (
                    <span key={a.id}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#1FB6C1]/15 text-[#1FB6C1] border border-[#1FB6C1]/30"
                          data-testid={`voice-${kind}-assignee-${a.id}`}>
                      {a.name}
                      <button type="button" onClick={() => removeAssignee(a.id)} className="opacity-70 hover:opacity-100">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                {unmatched.length > 0 && (
                  <div className="mt-1 text-[10px] text-[#F59E0B] flex items-center gap-1" data-testid={`voice-${kind}-unmatched`}>
                    <AlertCircle className="w-3 h-3" />
                    Couldn&apos;t match: {unmatched.join(", ")}. Edit assignees on the Tasks page after creating.
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 hv-keep-cols">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#647087]">Priority</label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className="hv-input mt-1 text-sm"
                    data-testid={`voice-${kind}-priority`}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#647087] flex items-center gap-1">
                    <CalIcon className="w-3 h-3" /> Due date
                  </label>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="hv-input mt-1 text-sm"
                    data-testid={`voice-${kind}-due`}
                  />
                </div>
              </div>
            </>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#647087]">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="hv-input mt-1 text-sm"
              placeholder="Detail / context (the full transcription lands here)…"
              data-testid={`voice-${kind}-description`}
            />
          </div>

          {err && (
            <div className="text-xs text-rose-300 border border-rose-500/30 bg-rose-500/10 rounded px-2 py-1.5" data-testid={`voice-${kind}-err`}>
              {err}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
            <button
              type="button"
              onClick={close}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid={`voice-${kind}-cancel`}
            >
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
            {/* [Iter 290zp.31] Single-action bottom button.
                • Recording   → "Done" (stops the mic + starts
                  transcribe). Replaces the old top-of-modal "Stop &
                  transcribe" button.
                • Transcribing/parsing → same button, disabled with
                  spinner, so the rep can see something is happening.
                • Idle (form ready or user typed manually) → normal
                  "Create task/ticket" submit. */}
            {recording ? (
              <button
                type="button"
                onClick={stopRec}
                className="hv-btn hv-btn-primary bg-rose-500 hover:bg-rose-400 text-xs"
                data-testid={`voice-${kind}-done`}
              >
                <Mic className="w-3.5 h-3.5 animate-pulse" /> Done
              </button>
            ) : transcribing || parsing ? (
              <button
                type="button"
                disabled
                className="hv-btn hv-btn-primary text-xs opacity-70 cursor-not-allowed"
                data-testid={`voice-${kind}-processing`}
              >
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {transcribing ? "Transcribing…" : "Filling task…"}
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={busy || !title.trim()}
                className="hv-btn hv-btn-primary text-xs"
                data-testid={`voice-${kind}-submit`}
              >
                <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : `Save ${kind}`}
              </button>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
